<template>
    <div class="module-selection-page">
        <!-- Background Shades -->
        <div class="bg-shade shape-1"></div>
        <div class="bg-shade shape-2"></div>

        <div class="container position-relative z-index-1">
            <div class="text-center mb-5 fade-in-down">
                <h1 class="main-title mb-2">SMARTCAT V4</h1>
                <p class="subtitle text-secondary">Select a module to continue</p>
            </div>

            <div class="row justify-content-center g-4">
                <div
v-for="(module, index) in modules" :key="index" class="col-xl-3 col-lg-4 col-md-6 fade-in-up"
                    :style="{ animationDelay: `${index * 0.1}s` }">
                    <div class="card module-card border-0 h-100" @click="navigateTo(module.path, module.title)">
                        <div class="card-body text-center p-4 d-flex flex-column align-items-center">

                            <!-- Icon Container -->
                            <div class="icon-box mb-4 shadow-sm" :class="module.gradientClass">
                                <i :class="[module.icon, 'text-white']"></i>
                            </div>

                            <h5 class="fw-bold text-dark mb-1">{{ module.title }}</h5>
                            <p class="text-muted text-sm mb-4 flex-grow-1">{{ module.description }}</p>

                            <div class="action-btn">
                                <span class="text-xs font-weight-bold text-uppercase" :class="module.textClass">
                                    Access Module <i class="fas fa-arrow-right ms-1"></i>
                                </span>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "ModuleSelection",
    data() {
        return {
            modules: [
                {
                    title: "Material Management",
                    slug: "material-management",
                    description: "Material Catalog Request Processing",
                    path: "/dashboards/my-task",
                    icon: "ni ni-archive-2",
                    gradientClass: "bg-gradient-primary",
                    textClass: "text-primary"
                },
                {
                    title: "Asset Management",
                    slug: "manage-assets",
                    description: "Browse & Request Available Items",
                    path: "/assets-management",
                    icon: "ni ni-shop",
                    gradientClass: "bg-gradient-success",
                    textClass: "text-success"
                },
            ]
        };
    },
    mounted() {
        this.$store.commit("argonConfig/toggleDefaultLayout");
        document.body.classList.remove("bg-gray-100");
    },
    beforeUnmount() {
        this.$store.commit("argonConfig/toggleDefaultLayout");
        document.body.classList.add("bg-gray-100");
    },
    methods: {
        navigateTo(path, title) {
            const module = this.modules.find(m => m.title === title);
            this.$store.commit("auth/setSelectedModule", title);
            // Redirect to URL with slug prefix (e.g., /asset-management/dictionary/manage-assets)
            window.location.href = `/${module.slug}${path}`;
        }
    }
};
</script>

<style scoped>
/* Page Layout */
.module-selection-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f8f9fe;
    position: relative;
    overflow: hidden;
    padding: 2rem;
}

/* Background Shapes */
.bg-shade {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    /* Increased blur for smoother background */
    z-index: 0;
    opacity: 0.5;
}

.shape-1 {
    width: 500px;
    height: 500px;
    background: #5e72e4;
    top: -150px;
    right: -150px;
}

.shape-2 {
    width: 400px;
    height: 400px;
    background: #2dce89;
    bottom: -100px;
    left: -100px;
}

.z-index-1 {
    z-index: 1;
}

/* Typography */
.main-title {
    font-weight: 800;
    color: #32325d;
    letter-spacing: -0.5px;
}

.subtitle {
    font-size: 1.1rem;
    font-weight: 400;
}

/* Card Styling */
.module-card {
    background: rgba(255, 255, 255, 0.85);
    /* Glass feel */
    backdrop-filter: blur(12px);
    border-radius: 1.5rem;
    /* Rounder corners */
    box-shadow: 0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08);
    /* Soft shadow */
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    cursor: pointer;
    overflow: hidden;
    position: relative;
    top: 0;
}

.module-card:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(50, 50, 93, 0.1), 0 5px 15px rgba(0, 0, 0, 0.07);
}

/* Icon Box */
.icon-box {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    margin-bottom: 1.5rem;
    transition: transform 0.3s ease;
}

.module-card:hover .icon-box {
    transform: scale(1.1) rotate(5deg);
}

/* Gradients */
.bg-gradient-primary {
    background: linear-gradient(87deg, #5e72e4 0, #825ee4 100%);
}

.bg-gradient-success {
    background: linear-gradient(87deg, #2dce89 0, #2dcecc 100%);
}

.bg-gradient-warning {
    background: linear-gradient(87deg, #fb6340 0, #fbb140 100%);
}

.bg-gradient-info {
    background: linear-gradient(87deg, #11cdef 0, #1171ef 100%);
}

/* Animations */
.fade-in-down {
    animation: fadeInDown 0.8s ease-out;
}

.fade-in-up {
    will-change: transform, opacity;
    animation: fadeInUp 0.8s ease-out forwards;
    opacity: 0;
    transform: translateY(20px);
}

@keyframes fadeInDown {
    from {
        opacity: 0;
        transform: translateY(-20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Utilities */
.text-sm {
    font-size: 0.875rem;
}

.text-xs {
    font-size: 0.75rem;
}

.font-weight-bold {
    font-weight: 600;
}
</style>
